
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/gift-guides', label: 'Gift Guides' },
    { to: '/reviews', label: 'Reviews' },
    { to: '#newsletter', label: 'Newsletter', isAnchor: true }
  ];

  const handleAnchorClick = (e: React.MouseEvent, anchor: string) => {
    e.preventDefault();
    const element = document.querySelector(anchor);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-3' 
          : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
          >
            <img 
              src="https://public.readdy.ai/ai/img_res/d60c6eac-2a5e-4b32-8553-ec512239e7e6.png" 
              alt="Best Christmas Gift Reviews" 
              className="h-10 w-auto group-hover:scale-110 transition-transform duration-300"
            />
            <span className={`font-bold text-xl transition-colors duration-300 ${
              isScrolled ? 'text-[#1B4332]' : 'text-white'
            }`}>
              Best Christmas Gift Reviews
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link, index) => (
              link.isAnchor ? (
                <a
                  key={index}
                  href={link.to}
                  onClick={(e) => handleAnchorClick(e, link.to)}
                  className={`font-medium transition-all duration-300 relative group cursor-pointer ${
                    isScrolled ? 'text-gray-700 hover:text-[#8B2635]' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {link.label}
                  <span className={`absolute -bottom-1 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full ${
                    isScrolled ? 'bg-[#8B2635]' : 'bg-[#D4AF37]'
                  }`}></span>
                </a>
              ) : (
                <Link
                  key={index}
                  to={link.to}
                  className={`font-medium transition-all duration-300 relative group ${
                    isScrolled ? 'text-gray-700 hover:text-[#8B2635]' : 'text-white/90 hover:text-white'
                  } ${location.pathname === link.to ? (isScrolled ? 'text-[#8B2635]' : 'text-white') : ''}`}
                >
                  {link.label}
                  <span className={`absolute -bottom-1 left-0 h-0.5 transition-all duration-300 ${
                    location.pathname === link.to ? 'w-full' : 'w-0 group-hover:w-full'
                  } ${isScrolled ? 'bg-[#8B2635]' : 'bg-[#D4AF37]'}`}></span>
                </Link>
              )
            ))}
            <Link
              to="/gift-guides"
              className={`px-6 py-2.5 rounded-full font-semibold transition-all duration-300 whitespace-nowrap btn-festive ${
                isScrolled 
                  ? 'bg-gradient-to-r from-[#8B2635] to-[#A52A3A] text-white hover:shadow-lg hover:shadow-[#8B2635]/30 hover:scale-105' 
                  : 'bg-white/10 backdrop-blur-sm text-white border border-white/30 hover:bg-white/20'
              }`}
            >
              <span className="flex items-center gap-2">
                Start Shopping
                <i className="ri-sparkling-fill"></i>
              </span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${
              isScrolled ? 'text-[#1B4332] hover:bg-gray-100' : 'text-white hover:bg-white/10'
            }`}
          >
            <i className={`text-2xl transition-transform duration-300 ${isMobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-500 ${
            isMobileMenuOpen ? 'max-h-96 opacity-100 mt-6' : 'max-h-0 opacity-0'
          }`}
        >
          <div className={`rounded-2xl p-6 space-y-4 ${
            isScrolled ? 'bg-gray-50' : 'bg-white/10 backdrop-blur-md'
          }`}>
            {navLinks.map((link, index) => (
              link.isAnchor ? (
                <a
                  key={index}
                  href={link.to}
                  onClick={(e) => handleAnchorClick(e, link.to)}
                  className={`block py-2 font-medium transition-all duration-300 ${
                    isScrolled ? 'text-gray-700 hover:text-[#8B2635] hover:translate-x-2' : 'text-white hover:text-[#D4AF37] hover:translate-x-2'
                  }`}
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={index}
                  to={link.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-2 font-medium transition-all duration-300 ${
                    isScrolled ? 'text-gray-700 hover:text-[#8B2635] hover:translate-x-2' : 'text-white hover:text-[#D4AF37] hover:translate-x-2'
                  }`}
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  {link.label}
                </Link>
              )
            ))}
            <Link
              to="/gift-guides"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-center px-6 py-3 bg-gradient-to-r from-[#8B2635] to-[#A52A3A] text-white rounded-full font-semibold hover:shadow-lg transition-all duration-300"
            >
              Start Shopping
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
